import { Formik, Form } from "formik";

import {
  Box,
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import MDButton from "components/MDButton";
import FormField from "layouts/pages/account/components/FormField";
import { setMessage, useMaterialUIController } from "context";

import {
  createTamanhoCamiseta,
  editTamanhoCamiseta,
} from "services/kitsCamiseta";
import { IEventoResponse } from "interfaces/eventos";
import { ICamiseta } from "interfaces/kitsCamiseta";

import {
  initialValuesCamiseta,
  initialValuesEditCamiseta,
} from "../schemas/initialValuesFormKits";
import formFieldCamiseta from "../schemas/formFieldKits";
import { validationCamiseta } from "../schemas/validationsKits";
import {
  KITS_ACCENT,
  kitsFieldSx,
  kitsModalHeaderSx,
  kitsModalPaperSx,
} from "../kits-evento.theme";

interface ModalAddEditKitsProps {
  evento: IEventoResponse;
  open: boolean;
  kit: ICamiseta | null;
  updateEvento: () => void;
  handleClose: () => void;
}

export default function ModalAddEditKits({
  open,
  handleClose,
  updateEvento,
  kit,
  evento,
}: ModalAddEditKitsProps) {
  const [, dispatch] = useMaterialUIController();
  const { tamanho, quantidade } = formFieldCamiseta;

  const formCamiseta = kit
    ? initialValuesEditCamiseta({ ...kit, eventId: evento.id })
    : initialValuesCamiseta(evento.id);

  const handleSubmitCamiseta = async (params: {
    id?: number;
    tamanho: string;
    quantidade: number;
    eventId: number;
  }) => {
    try {
      if (params?.id) {
        await editTamanhoCamiseta(params);
      } else {
        await createTamanhoCamiseta([params]);
      }
      updateEvento();
      handleClose();
    } catch (error: unknown) {
      setMessage(dispatch, {
        open: true,
        text:
          error instanceof Error
            ? error.message
            : params?.id
              ? "Erro ao editar camiseta"
              : "Erro ao criar camiseta",
        severity: "error",
      });
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      fullWidth
      PaperProps={{ sx: kitsModalPaperSx }}
    >
      <Box sx={kitsModalHeaderSx}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Box
            component="img"
            src={evento.urlImageDestaque}
            alt={evento.titulo}
            sx={{
              width: 56,
              height: 56,
              borderRadius: 1.5,
              objectFit: "cover",
              border: "2px solid rgba(255,255,255,0.2)",
              flexShrink: 0,
            }}
          />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="overline"
              sx={{ opacity: 0.75, letterSpacing: 1 }}
            >
              {kit ? "Editar tamanho" : "Novo tamanho"}
            </Typography>
            <Typography variant="subtitle1" fontWeight={700} noWrap>
              {evento.titulo}
            </Typography>
          </Box>
          <IconButton
            onClick={handleClose}
            sx={{ color: "inherit" }}
            aria-label="Fechar"
          >
            <CloseIcon />
          </IconButton>
        </Stack>
      </Box>

      <DialogContent sx={{ p: { xs: 2, sm: 2.5 }, overflow: "auto" }}>
        <Formik
          enableReinitialize
          initialValues={formCamiseta}
          validationSchema={validationCamiseta}
          onSubmit={(values) => {
            void handleSubmitCamiseta({
              id: values.id as number | undefined,
              tamanho: String(values.tamanho),
              quantidade: Number(values.quantidade),
              eventId: Number(values.eventId),
            });
          }}
        >
          {({ values, handleChange, isSubmitting }) => (
            <Form id="form-camiseta">
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormField
                    variant="outlined"
                    name={tamanho.name}
                    type={tamanho.type}
                    label={tamanho.label}
                    required
                    value={values.tamanho}
                    onChange={handleChange}
                    sx={kitsFieldSx}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormField
                    variant="outlined"
                    name={quantidade.name}
                    type={quantidade.type}
                    label={quantidade.label}
                    required
                    value={values.quantidade}
                    onChange={handleChange}
                    sx={kitsFieldSx}
                  />
                </Grid>
              </Grid>

              <Stack
                direction="row"
                spacing={1}
                justifyContent="flex-end"
                mt={3}
              >
                <MDButton variant="outlined" color="dark" onClick={handleClose}>
                  Cancelar
                </MDButton>
                <MDButton
                  variant="gradient"
                  color="dark"
                  type="submit"
                  disabled={isSubmitting}
                  sx={{
                    background: `linear-gradient(195deg, ${KITS_ACCENT}, #e04a1a)`,
                  }}
                >
                  {isSubmitting ? "Salvando…" : "Salvar"}
                </MDButton>
              </Stack>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
}
